import * as React from "react";
import { Alert, Text, TextLink } from "@origin-digital/ods-core";

type NmiAlertConfig = {
  title?: string;
  message: React.ReactNode;
  tone?: "caution";
};

export enum NmiStatusEnum {
  NOT_FOUND = "NOT_FOUND",
  ASP = "ASP",
  NO_ACCOUNT = "NO_ACCOUNT",
  NOT_RETAILER = "NOT_RETAILER",
  NMI_X = "NMI_X",
  NMI_G = "NMI_G",
  NMI_N = "NMI_N",
  IS_OCCUPIER = "IS_OCCUPIER",
  ELIGIBLE = "ELIGIBLE",
  CITIPOWER = "CITIPOWER",
  POWERCOR = "POWERCOR",
  UNITED = "UNITED",
  ERGON = "ERGON",
  ENERGEX = "ENERGEX",
  EVO = "EVO",
  EASTERN = "EASTERN",
  EASTERN_CHANGE_SUPPLY = "EASTERN_CHANGE_SUPPLY",
  EASTERN_REMOVE_SUPPLY = "EASTERN_REMOVE_SUPPLY",
}

export enum MirnStatusEnum {
  NOT_FOUND = "NOT_FOUND",
  NO_ACCOUNT = "NO_ACCOUNT",
  NOT_RETAILER = "NOT_RETAILER",
  ELIGIBLE = "ELIGIBLE",
  IS_OCCUPIER = "IS_OCCUPIER",
}

export const NMI_STATUS_ALERTS: Partial<Record<NmiStatusEnum, NmiAlertConfig>> =
  {
    [NmiStatusEnum.ELIGIBLE]: {
      message: (
        <Text>
          Please check if the site address is correct before proceeding.
        </Text>
      ),
      tone: "caution",
    },
    [NmiStatusEnum.ASP]: {
      title: "Sorry, you can’t use this form to remove supply at this address.",
      message: (
        <Text>
          To remove the electricity supply you will need to contact an
          Accredited Service Provider.
        </Text>
      ),
    },
    [NmiStatusEnum.NO_ACCOUNT]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: (
        <Text>
          An active Origin account is required. Click{" "}
          <TextLink
            href="https://www.originenergy.com.au/moving/"
            target="_blank"
          >
            here
          </TextLink>{" "}
          to set up an account first.
        </Text>
      ),
    },
    [NmiStatusEnum.NOT_RETAILER]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: (
        <Text>
          Origin is not the retailer for this site, please contact the current
          retailer to change or remove the electricity supply.
        </Text>
      ),
    },
    [NmiStatusEnum.NMI_X]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: <Text>Supply is already abolished.</Text>,
    },
    [NmiStatusEnum.NMI_G]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: (
        <Text>
          There is an in-progress new connection at this site, please email
          hello@origin.com.au.
        </Text>
      ),
    },
    [NmiStatusEnum.NMI_N]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: (
        <Text>
          This site is part of an embedded network, please email
          centralisedenergyops@originenergy.com.au.
        </Text>
      ),
    },
    [NmiStatusEnum.IS_OCCUPIER]: {
      title:
        "Sorry, you can't use this form to change or remove supply at this address.",
      message: (
        <Text>
          There is an in-progress move out, please contact us on 132 461 to
          cancel it first.
        </Text>
      ),
    },
    [NmiStatusEnum.ENERGEX]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Energex.",
      message: (
        <>
          Use the{" "}
          <TextLink
            href="https://apps.energex.com.au/epp/Public/SelfService"
            target="_blank"
          >
            Energex
          </TextLink>{" "}
          Portal to change the electricity supply in the Energex area.
        </>
      ),
    },
    [NmiStatusEnum.CITIPOWER]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Citipower.",
      message: (
        <Text>
          Use the{" "}
          <TextLink
            href="https://econnect.portal.powercor.com.au/customer/loginpage"
            target="_blank"
          >
            eConnect
          </TextLink>{" "}
          Portal to change the electricity supply in the Citipower area.
        </Text>
      ),
    },
    [NmiStatusEnum.POWERCOR]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Powercor.",
      message: (
        <Text>
          Use the{" "}
          <TextLink
            href="https://econnect.portal.powercor.com.au/customer/loginpage"
            target="_blank"
          >
            eConnect
          </TextLink>{" "}
          Portal to change the electricity supply in the Powercor area.
        </Text>
      ),
    },
    [NmiStatusEnum.UNITED]: {
      title:
        "Sorry, you can't use this form to change or remove supply for United Energy.",
      message: (
        <Text>
          Use the{" "}
          <TextLink href="https://myenergy.ue.com.au/" target="_blank">
            MyEnergy
          </TextLink>{" "}
          Portal to change the electricity supply in the United Energy area.
        </Text>
      ),
    },
    [NmiStatusEnum.ERGON]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Ergon.",
      message: (
        <Text>
          Submit a request to{" "}
          <TextLink
            href="https://www.ergon.com.au/network/our-services/our-portals"
            target="_blank"
          >
            Ergon
          </TextLink>{" "}
          to change or remove the electricity supply in the Ergon area.
        </Text>
      ),
    },
    [NmiStatusEnum.EVO]: {
      title: "Sorry, you can't use this form to remove supply for Evo Energy.",
      message: (
        <Text>
          You will need to submit a request directly with{" "}
          <TextLink
            href="https://www.evoenergy.com.au/Forms/SupplyAbolishment"
            target="_blank"
          >
            Evo Energy.
          </TextLink>
        </Text>
      ),
    },
    [NmiStatusEnum.EASTERN_CHANGE_SUPPLY]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Ausnet.",
      message: (
        <Text>
          Use the{" "}
          <TextLink
            href="https://www.ausnetservices.com.au/electricity/connections/energyconnect-application"
            target="_blank"
          >
            EnergyConnect
          </TextLink>{" "}
          Portal to change the electricity supply in the AusNet area. For a
          solar metering request, please email the Electrical Works Request and
          Certificate of Electrical Safety to{" "}
          <TextLink href="mailto:metering.ops@originenergy.com.au">
            metering.ops@originenergy.com.au
          </TextLink>
          .
        </Text>
      ),
    },
    [NmiStatusEnum.EASTERN_REMOVE_SUPPLY]: {
      title:
        "Sorry, you can't use this form to change or remove supply for Ausnet.",
      message: (
        <Text>
          Use the{" "}
          <TextLink
            href="https://www.ausnetservices.com.au/electricity/connections/energyconnect-application"
            target="_blank"
          >
            EnergyConnect
          </TextLink>{" "}
          Portal to remove the electricity supply in the AusNet area.
        </Text>
      ),
    },
  };

export const MIRN_STATUS_ALERTS: Partial<
  Record<MirnStatusEnum, NmiAlertConfig>
> = {
  [MirnStatusEnum.ELIGIBLE]: {
    message: (
      <Text>Please check if the address is correct before proceeding.</Text>
    ),
    tone: "caution",
  },
  [MirnStatusEnum.NOT_FOUND]: {
    title: "MIRN not found",
    message: (
      <Text>
        Check that the MIRN entered is correct. If it is not a typo, it might be
        with another retailer.
      </Text>
    ),
  },
  [MirnStatusEnum.NO_ACCOUNT]: {
    title:
      "Sorry, you can't use this form to change or remove supply at this address.",
    message: (
      <Text>
        An active Origin account is required. Click here to set up an account
        first.
      </Text>
    ),
  },
  [MirnStatusEnum.NOT_RETAILER]: {
    title:
      "Sorry, you can't use this form to change or remove supply at this address.",
    message: (
      <Text>
        Origin is not the retailer for this site, please contact the current
        retailer to change or remove the gas supply.
      </Text>
    ),
  },
  [MirnStatusEnum.IS_OCCUPIER]: {
    title:
      "Sorry, you can't use this form to change or remove supply at this address.",
    message: (
      <Text>
        There is an in-progress move out, please contact us on 132 461 to cancel
        it first.
      </Text>
    ),
  },
};

const StatusAlert: React.FC<{
  status: NmiStatusEnum | MirnStatusEnum;
  type: "nmi" | "mirn";
  requestType?: "CHANGE_SUPPLY" | "REMOVE_SUPPLY";
}> = ({ status, type, requestType }) => {
  if (type === "nmi" && status === NmiStatusEnum.EASTERN) {
    status = `${status}_${requestType}` as NmiStatusEnum;
  }
  const config =
    type === "nmi"
      ? NMI_STATUS_ALERTS[status as NmiStatusEnum]
      : MIRN_STATUS_ALERTS[status as MirnStatusEnum];

  if (!config) return null;

  return (
    <Alert title={config.title} tone={config.tone}>
      {config.message}
    </Alert>
  );
};

export default StatusAlert;
